// src/services/supabaseService.js
import { createClient } from '@supabase/supabase-js';
import {
  StateModel,
  Profession,
  Provider,
  Client,
  Service,
  Request,
  Conversation,
  Message,
  AppNotification,
  MonthlyPayment,
} from '../models';

import offlineStorage from './offlineStorage';
import cloudinaryService from './cloudinaryService';

const supabaseUrl = 'https://reescgwvnnbvwgtadvdv.supabase.co';
const supabaseAnonKey =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJlZXNjZ3d2bm5idndndGFkdmR2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjMzODYwOTUsImV4cCI6MjA3ODk2MjA5NX0.kU6E7PW9pxC0uf1DGD266uLoLfYtYZUYrSnhOxPBb5Q';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    redirectTo:
      typeof window !== 'undefined' ? `${window.location.origin}/` : '',
  },
  global: {
    headers: { 'X-Client-Info': 'supabase-js/2.39.7' },
  },
});

class SupabaseService {
  constructor() {
    this.client = supabase;
    this._currentUser = null;
    this._isInitializing = false;
    this._initPromise = null;
    if (typeof window !== 'undefined') this._initPromise = this.init();
  }

  // ── Authentication ─────────────────────────────────────────

  async handlePasswordRecoveryToken(accessToken, refreshToken = '') {
    await this.waitForInit();
    try {
      const { data: sessionData, error: sessionError } =
        await this.client.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
      if (sessionError) {
        const { data: { session }, error: getSessionError } =
          await this.client.auth.getSession();
        if (getSessionError) return { success: false, error: 'Failed to establish session' };
        if (session) return { success: true, session };
        return { success: false, error: sessionError.message };
      }
      if (sessionData?.session) return { success: true, session: sessionData.session };
      return { success: false, error: 'No session established' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async updatePasswordWithSessionCheck(newPassword) {
    await this.waitForInit();
    try {
      const { data: { session }, error: sessionError } =
        await this.client.auth.getSession();
      if (sessionError || !session)
        throw new Error('Invalid or expired session. Please request a new password reset link.');
      const { data, error } = await this.client.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      throw error;
    }
  }

  async init() {
    if (this._isInitializing) return;
    this._isInitializing = true;
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const { data: { session }, error } = await this.client.auth.getSession();
      if (error) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.clear();
        }
      } else if (session?.user) {
        this._currentUser = session.user;
      }

      if (!session?.user && !navigator.onLine) {
        try {
          const cached = await offlineStorage.loadSession();
          if (cached) this._currentUser = cached.session.user;
        } catch (_) {}
      }

      const { data: authListener } = this.client.auth.onAuthStateChange(
        async (event, session) => {
          switch (event) {
            case 'SIGNED_IN':
              if (session) {
                this._currentUser = session.user;
                offlineStorage.saveSession(session.user.id, {
                  user: session.user,
                  access_token: session.access_token,
                  refresh_token: session.refresh_token,
                  expires_at: session.expires_at,
                });
              }
              break;
            case 'SIGNED_OUT':
              this._currentUser = null;
              if (typeof window !== 'undefined') {
                localStorage.removeItem('supabase.auth.token');
                sessionStorage.clear();
              }
              break;
          }
        }
      );
      this._authListener = authListener;
    } catch (error) {
      console.error('❌ Error initializing Supabase service:', error);
    } finally {
      this._isInitializing = false;
      this._initPromise = null;
    }
  }

  async waitForInit() {
    if (typeof window === 'undefined') return;
    if (this._initPromise) {
      await this._initPromise;
      return;
    }
    while (this._isInitializing) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  get currentUser() {
    return this._currentUser;
  }

  async signUp(email, password, metadata = {}) {
    await this.waitForInit();
    try {
      const result = await this.client.auth.signUp({
        email,
        password,
        options: { data: metadata, emailRedirectTo: undefined, captchaToken: null },
      });
      if (result.error) throw result.error;
      return result;
    } catch (error) {
      throw error;
    }
  }

  async signIn(email, password) {
    await this.waitForInit();
    try {
      const result = await this.client.auth.signInWithPassword({ email, password });
      if (result.error) throw result.error;
      if (result.user) {
        this._currentUser = result.user;
        if (result.data?.session) {
          await offlineStorage.saveSession(result.data.user.id, {
            user: result.data.user,
            access_token: result.data.session.access_token,
            refresh_token: result.data.session.refresh_token,
            expires_at: result.data.session.expires_at,
          });
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'USER_LOGGED_IN' });
          }
        }
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async signInWithGoogle() {
    await this.waitForInit();
    try {
      const redirectTo =
        typeof window !== 'undefined'
          ? `${window.location.origin}/auth`
          : 'https://khidma4u.netlify.app/auth';
      const { data, error } = await this.client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  async signOut() {
    try {
      await this.client.auth.signOut();
      const offlineUserId = this._currentUser?.id;
      if (offlineUserId) await offlineStorage.clearAllUserData(offlineUserId);
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'USER_LOGGED_OUT' });
      }
      this._currentUser = null;
      if (typeof window !== 'undefined') {
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.clear();
        document.cookie.split(';').forEach((cookie) => {
          const name = cookie.split('=')[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
      }
    } catch (error) {
      throw error;
    }
  }

  async verifyOtp(email, token, type = 'email') {
    await this.waitForInit();
    try {
      const { data, error } = await this.client.auth.verifyOtp({ email, token, type });
      if (error) throw error;
      if (data.session) this._currentUser = data.session.user;
      return data;
    } catch (error) {
      throw error;
    }
  }

  async getSession() {
    await this.waitForInit();
    return await this.client.auth.getSession();
  }

  // ── Data Fetching ──────────────────────────────────────────

  async getStates() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('states')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data.map((json) => StateModel.fromJson(json));
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  }

  // ✅ مطابقة Dart — تشمل is_store و is_digital_products
  async getProfessions() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('professions')
        .select('id, name, is_store, is_digital_products')
        .order('name');
      if (error) throw error;
      return data.map((json) => Profession.fromJson(json));
    } catch (error) {
      console.error('Error fetching professions:', error);
      return [];
    }
  }

  // ✅ مطابقة Dart — تشمل is_digital_products في professions
  async getProviders({ stateId, professionId } = {}) {
    await this.waitForInit();
    try {
      let query = this.client
        .from('providers')
        .select('*, states(name), professions(name, is_store, is_digital_products)')
        .eq('is_verified', true)
        .eq('available', true)
        .order('created_at', { ascending: false });
      if (stateId) query = query.eq('state_id', stateId);
      if (professionId) query = query.eq('profession_id', professionId);
      const { data, error } = await query;
      if (error) throw error;
      return data.map((json) => {
        if (json.professions) {
          json.is_digital_products = json.professions.is_digital_products ?? false;
          json.is_store             = json.professions.is_store             ?? false;
          json.profession_name      = json.professions.name ?? json.profession_name ?? '';
        }
        if (json.states) {
          json.state_name = json.states.name ?? json.state_name ?? '';
        }
        return Provider.fromJson(json);
      });
    } catch (error) {
      console.error('Error fetching providers:', error);
      return [];
    }
  }

  // ✅ مطابقة Dart — تشمل is_digital_products
  async getProviderById(id) {
    await this.waitForInit();
    if (!navigator.onLine) {
      const cached = await offlineStorage.loadProfile(id);
      if (cached) return Provider.fromJson(cached);
      return null;
    }
    try {
      const queryPromise = this.client
        .from('providers')
        .select('*, states(name), professions(name, is_store, is_digital_products)')
        .eq('id', id)
        .maybeSingle();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Provider query timeout')), 3000)
      );
      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('permission denied')) {
          const altResult = await this.client
            .from('providers')
            .select('id, full_name, email')
            .eq('id', id)
            .maybeSingle();
          if (altResult.data) return Provider.fromJson(altResult.data);
        }
        return null;
      }
      if (data) {
        if (data.professions) {
          data.is_digital_products = data.professions.is_digital_products ?? false;
          data.is_store             = data.professions.is_store             ?? false;
          data.profession_name      = data.professions.name ?? data.profession_name ?? '';
        }
        if (data.states) {
          data.state_name = data.states.name ?? data.state_name ?? '';
        }
        const provider = Provider.fromJson(data);
        if (this._currentUser?.id === id) await offlineStorage.saveProfile(id, data);
        return provider;
      }
      return null;
    } catch (error) {
      const cached = await offlineStorage.loadProfile(id);
      if (cached) return Provider.fromJson(cached);
      return null;
    }
  }

  // ✅ مطابقة Dart — getProviderByShortId
  async getProviderByShortId(shortId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('providers')
        .select('*, states(name), professions(name, is_store, is_digital_products)')
        .eq('provider_id', shortId)
        .maybeSingle();
      if (error) throw error;
      if (data?.professions) {
        data.is_digital_products = data.professions.is_digital_products ?? false;
        data.is_store             = data.professions.is_store             ?? false;
        data.profession_name      = data.professions.name ?? data.profession_name ?? '';
      }
      if (data?.states) {
        data.state_name = data.states.name ?? data.state_name ?? '';
      }
      return data ? Provider.fromJson(data) : null;
    } catch (error) {
      console.error('getProviderByShortId error:', error);
      return null;
    }
  }

  async getClientById(id) {
    await this.waitForInit();
    if (!navigator.onLine) {
      const cached = await offlineStorage.loadProfile(id);
      if (cached) return Client.fromJson(cached);
      return null;
    }
    try {
      const queryPromise = this.client
        .from('clients')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Client query timeout')), 3000)
      );
      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
      if (error) {
        const cached = await offlineStorage.loadProfile(id);
        return cached ? Client.fromJson(cached) : null;
      }
      if (data) {
        const client = Client.fromJson(data);
        if (this._currentUser?.id === id) await offlineStorage.saveProfile(id, data);
        return client;
      }
      return null;
    } catch (error) {
      const cached = await offlineStorage.loadProfile(id);
      if (cached) return Client.fromJson(cached);
      return null;
    }
  }

  // ✅ مطابقة Dart — getClientByShortId
  async getClientByShortId(shortId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('clients')
        .select('*')
        .eq('client_id', shortId)
        .maybeSingle();
      if (error) throw error;
      return data ? Client.fromJson(data) : null;
    } catch (error) {
      console.error('getClientByShortId error:', error);
      return null;
    }
  }

  // ✅ مطابقة Dart — تشمل جميع حقول الخدمات الرقمية
  async getServicesForProvider(providerId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('services')
        .select(
          '*, categories(name), digital_category_id, product_type, file_url, preview_url, preview_images, media_urls'
        )
        .eq('provider_id', providerId)
        .eq('is_active', true);
      if (error) throw error;
      return data.map((json) => {
        const service = Service.fromJson(json);
        if (!service.imageUrl && json.image_url) service.imageUrl = json.image_url;
        return service;
      });
    } catch (error) {
      console.error('Error fetching services:', error);
      return [];
    }
  }

  async getRequestsForUser(userId, { isClient = true } = {}) {
    await this.waitForInit();
    if (!navigator.onLine) {
      const cached = await offlineStorage.loadRequests(userId);
      if (cached) return cached;
      return [];
    }
    try {
      const columnName = isClient ? 'client_id' : 'provider_id';
      const { data, error } = await this.client
        .from('requests')
        .select('*, services(*), clients(*), providers(*)')
        .eq(columnName, userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const requests = data.map((json) => Request.fromJson(json));
      await offlineStorage.saveRequests(userId, requests.map((r) => (r.toJson ? r.toJson() : r)));
      return requests;
    } catch (error) {
      console.error('Error fetching requests:', error);
      const cached = await offlineStorage.loadRequests(userId);
      return cached || [];
    }
  }

  // ✅ مطابقة Dart — getRequestById
  async getRequestById(requestId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('requests')
        .select('*, services(*), clients(*, states(name)), providers(*, states(name))')
        .eq('id', requestId)
        .maybeSingle();
      if (error) throw error;
      return data ? Request.fromJson(data) : null;
    } catch (error) {
      console.error('getRequestById error:', error);
      return null;
    }
  }

  // ✅ مطابقة Dart — getLastWithdrawalRequest
  async getLastWithdrawalRequest(providerId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('withdrawal_requests')
        .select('created_at')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('getLastWithdrawalRequest error:', error);
      return null;
    }
  }

  // ✅ مطابقة Dart — createWithdrawalRequest
  async createWithdrawalRequest({ providerId, amount }) {
    await this.waitForInit();
    const { data: bank, error: bankError } = await this.client
      .from('provider_bank_accounts')
      .select('id')
      .eq('provider_id', providerId)
      .eq('is_approved', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (bankError || !bank)
      throw new Error('لا يوجد حساب بنكي موافق عليه. أضف حسابك البنكي أولاً.');
    const { error } = await this.client.from('withdrawal_requests').insert({
      provider_id: providerId,
      bank_account_id: bank.id,
      amount,
      status: 'pending',
    });
    if (error) throw error;
  }

  // ── Data Insertion / Update ────────────────────────────────

  async createProvider(provider) {
    await this.waitForInit();
    try {
      const providerData = provider.toJson ? provider.toJson() : provider;
      const { error } = await this.client.from('providers').insert(providerData);
      if (error) throw new Error(`فشل إنشاء مزود الخدمة: ${error.message}`);
      const verification = await this.getProviderById(providerData.id);
      if (!verification)
        throw new Error('فشل التحقق من إنشاء مزود الخدمة. قد تكون هناك مشكلة في سياسات RLS');
      return verification;
    } catch (error) {
      throw error;
    }
  }

  async createClient(clientData) {
    await this.waitForInit();
    try {
      const clientPayload = {
        id: clientData.id || clientData.userId,
        full_name: clientData.fullName || clientData.full_name || '',
        email: clientData.email || '',
        avatar_url: clientData.avatarUrl || clientData.avatar_url || '',
        state_id: clientData.stateId || clientData.state_id || null,
        address: clientData.address || '',
        phone: clientData.phone || '',
        client_id: clientData.clientId || clientData.client_id || null,
        card_number: clientData.cardNumber || clientData.card_number || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const { error } = await this.client.from('clients').insert(clientPayload);
      if (error) throw new Error(`فشل إنشاء العميل: ${error.message}`);
      const verification = await this.getClientById(clientPayload.id);
      if (!verification)
        throw new Error('فشل التحقق من إنشاء العميل. قد تكون هناك مشكلة في سياسات RLS');
      return verification;
    } catch (error) {
      throw error;
    }
  }

  async updateProvider(id, data) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('providers')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  async deleteProviderAccount() {
    await this.waitForInit();
    try {
      const { error } = await this.client.rpc('delete_my_provider_account');
      if (error) throw new Error('فشل تنفيذ عملية الحذف: ' + error.message);
      await this.client.auth.signOut();
      return true;
    } catch (error) {
      throw error;
    }
  }

  async updateClient(id, data) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('clients')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — addService مع جميع الحقول الرقمية
  async addService({
    providerId,
    name,
    description,
    price,
    imageUrl = null,
    stock = null,
    categoryId = null,
    fileUrl = null,
    previewUrl = null,
    productType = null,
    digitalCategoryId = null,
    previewImages = null,
    mediaUrls = null,
    allowDownload = false,
  }) {
    await this.waitForInit();
    try {
      const payload = {
        provider_id: providerId,
        name,
        description,
        price,
        image_url: imageUrl,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      if (stock !== null) payload.stock = stock;
      if (categoryId !== null) payload.category_id = categoryId;
      if (fileUrl !== null) payload.file_url = fileUrl;
      if (previewUrl !== null) payload.preview_url = previewUrl;
      if (productType !== null) payload.product_type = productType;
      if (digitalCategoryId !== null) payload.digital_category_id = digitalCategoryId;
      if (previewImages !== null) payload.preview_images = previewImages;
      if (mediaUrls !== null) payload.media_urls = mediaUrls;
      payload.allow_download = allowDownload;

      const { data, error } = await this.client
        .from('services')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  async updateService(id, data) {
    await this.waitForInit();
    try {
      const dbData = { ...data };
      if ('imageUrl' in dbData) { dbData.image_url = dbData.imageUrl; delete dbData.imageUrl; }
      if ('fileUrl' in dbData) { dbData.file_url = dbData.fileUrl; delete dbData.fileUrl; }
      if ('previewUrl' in dbData) { dbData.preview_url = dbData.previewUrl; delete dbData.previewUrl; }
      if ('previewImages' in dbData) { dbData.preview_images = dbData.previewImages; delete dbData.previewImages; }
      if ('mediaUrls' in dbData) { dbData.media_urls = dbData.mediaUrls; delete dbData.mediaUrls; }
      if ('productType' in dbData) { dbData.product_type = dbData.productType; delete dbData.productType; }
      if ('digitalCategoryId' in dbData) { dbData.digital_category_id = dbData.digitalCategoryId; delete dbData.digitalCategoryId; }
      if ('categoryId' in dbData) { dbData.category_id = dbData.categoryId; delete dbData.categoryId; }
      if ('categoryName' in dbData) { delete dbData.categoryName; }
      if ('allowDownload' in dbData) { dbData.allow_download = dbData.allowDownload; delete dbData.allowDownload; }
      const { error } = await this.client
        .from('services')
        .update({ ...dbData, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — hasActiveRequests
  async hasActiveRequests(serviceId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('requests')
        .select('id')
        .eq('service_id', serviceId)
        .eq('is_active', true)
        .in('status', ['pending', 'accepted', 'pending_provider_confirmation'])
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data !== null;
    } catch (error) {
      console.error('hasActiveRequests error:', error);
      return false;
    }
  }

  // ✅ مطابقة Dart — hasAnyRequests
  async hasAnyRequests(serviceId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('requests')
        .select('id')
        .eq('service_id', serviceId)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data !== null;
    } catch (error) {
      console.error('hasAnyRequests error:', error);
      return false;
    }
  }

  // ✅ مطابقة Dart — hasDigitalPurchases
  async hasDigitalPurchases(serviceId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('digital_purchases')
        .select('id')
        .eq('service_id', serviceId)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data !== null;
    } catch (error) {
      console.error('hasDigitalPurchases error:', error);
      return false;
    }
  }

  // ✅ مطابقة Dart — soft_delete_service عبر RPC
  async deleteService(id) {
    await this.waitForInit();
    try {
      const userId = this._currentUser?.id;
      const { error } = await this.client.rpc('soft_delete_service', {
        p_service_id: id,
        p_provider_id: userId,
      });
      if (error) throw new Error(error.message);
    } catch (error) {
      const msg = error.message
        .replace('PostgreSQLException: ', '')
        .replace('Exception: ', '');
      throw new Error(msg);
    }
  }

  async createRequest(request) {
    await this.waitForInit();
    try {
      const clientId = request.clientId || request.client_id;
      const providerId = request.providerId || request.provider_id;
      const serviceId = request.serviceId || request.service_id;
      const status = request.status || 'pending';
      const paymentMethod = request.paymentMethod || request.payment_method || 'cash';
      const quantity = request.quantity || 1;

      if (!clientId || !providerId || !serviceId)
        throw new Error('Client ID, Provider ID, and Service ID cannot be null');

      const { data, error } = await this.client
        .from('requests')
        .insert({
          client_id: clientId,
          provider_id: providerId,
          service_id: serviceId,
          status,
          payment_method: paymentMethod,
          quantity,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    } catch (error) {
      throw error;
    }
  }

  async updateRequestStatus(requestId, status) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('requests')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', requestId);
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — clientConfirmCompletion
  async clientConfirmCompletion(requestId) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('requests')
        .update({ client_confirmed: true, updated_at: new Date().toISOString() })
        .eq('id', requestId);
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  async completeAndRateRequest(requestId, rating, review) {
    await this.waitForInit();
    try {
      const { data: requestData, error: fetchError } = await this.client
        .from('requests')
        .select('client_id, provider_id, services(name), clients(full_name, phone)')
        .eq('id', requestId)
        .single();
      if (fetchError) throw fetchError;

      const { error: updateError } = await this.client
        .from('requests')
        .update({
          status: 'pending_provider_confirmation',
          client_confirmed: true,
          rating,
          review,
          updated_at: new Date().toISOString(),
        })
        .eq('id', requestId);
      if (updateError) throw updateError;

      await this.createNotification({
        user_id: requestData.provider_id,
        title: 'طلب تأكيد إتمام خدمة',
        message: `يطلب العميل ${requestData.clients?.full_name || 'عميل'} تأكيد إتمام خدمة "${requestData.services?.name || 'خدمة'}".`,
        is_read: false,
      });
      return true;
    } catch (error) {
      throw error;
    }
  }

  async confirmRequestCompletion(requestId, confirmed) {
    if (confirmed) {
      await this.providerConfirmCompletion(requestId);
    } else {
      await this.providerRejectCompletion(requestId);
    }
  }

  // ✅ مطابقة Dart — providerRejectCompletion
  async providerRejectCompletion(requestId) {
    await this.waitForInit();
    try {
      const { data: requestData } = await this.client
        .from('requests')
        .select('client_id')
        .eq('id', requestId)
        .single();
      const { error } = await this.client
        .from('requests')
        .update({
          status: 'accepted',
          client_confirmed: false,
          provider_confirmed: false,
          rating: null,
          review: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', requestId);
      if (error) throw error;
      if (requestData?.client_id) {
        await this.createNotification({
          user_id: requestData.client_id,
          title: 'لم يتم تأكيد إتمام الخدمة',
          message: 'لم يوافق المزود على إتمام الخدمة. يرجى التواصل معه للتوضيح.',
          is_read: false,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — providerConfirmCompletion
  // ملاحظة: أرشفة الدردشة تتم الآن تلقائياً عبر DB trigger (archive_and_close_chat)
  // ولم تعد تُحذف — لذلك أُزيل كود حذف المحادثة من هنا
  async providerConfirmCompletion(requestId) {
    await this.waitForInit();
    try {
      const { data: requestData } = await this.client
        .from('requests')
        .select('client_id, provider_id')
        .eq('id', requestId)
        .single();
      const { error } = await this.client
        .from('requests')
        .update({
          status: 'completed',
          provider_confirmed: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', requestId);
      if (error) throw error;

      if (requestData?.client_id) {
        await this.createNotification({
          user_id: requestData.client_id,
          title: 'تم تأكيد إتمام الخدمة',
          message: 'قام المزود بتأكيد إتمام الخدمة بنجاح. شكراً لاستخدامك التطبيق!',
          is_read: false,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  async createProviderDocument(providerId, { idFrontUrl, idBackUrl, professionUrl }) {
    await this.waitForInit();
    try {
      const { error } = await this.client.from('providerdocuments').insert({
        provider_id: providerId,
        document_type: 'proof_of_profession',
        id_front_url: idFrontUrl,
        id_back_url: idBackUrl,
        profession_url: professionUrl,
        status: 'pending',
        created_at: new Date().toISOString(),
      });
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  async getProviderDocument(providerId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('providerdocuments')
        .select('id_front_url, id_back_url, profession_url, status, created_at')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('getProviderDocument error:', error);
      return null;
    }
  }

  async getProviderBankAccount(providerId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('provider_bank_accounts')
        .select('*')
        .eq('provider_id', providerId)
        .maybeSingle();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('getProviderBankAccount error:', error);
      return null;
    }
  }

  async saveProviderBankAccount(providerId, rib, chequeUrl) {
    await this.waitForInit();
    try {
      const existing = await this.getProviderBankAccount(providerId);
      if (existing) {
        const { data, error } = await this.client
          .from('provider_bank_accounts')
          .update({
            rib,
            cheque_url: chequeUrl,
            status: 'pending',
            is_approved: null,
            updated_at: new Date().toISOString()
          })
          .eq('provider_id', providerId)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await this.client
          .from('provider_bank_accounts')
          .insert({
            provider_id: providerId,
            rib,
            cheque_url: chequeUrl,
            status: 'pending',
            is_approved: null
          })
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('saveProviderBankAccount error:', error);
      throw error;
    }
  }


  // ── Chat ───────────────────────────────────────────────────

  async getConversationsForUser(userId) {
    await this.waitForInit();
    if (!navigator.onLine) {
      const cached = await offlineStorage.loadConversations(userId);
      if (cached) return cached;
      return [];
    }
    try {
      const { data, error } = await this.client
        .from('conversations')
        .select('*, clients(*, states(name)), providers(*, states(name), professions(name))')
        .or(`client_id.eq.${userId},provider_id.eq.${userId}`)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const conversations = data.map((json) => Conversation.fromJson(json));
      await offlineStorage.saveConversations(
        userId,
        conversations.map((c) => (c.toJson ? c.toJson() : c))
      );
      return conversations;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      const cached = await offlineStorage.loadConversations(userId);
      return cached || [];
    }
  }

  async getMessagesForConversation(conversationId) {
    await this.waitForInit();
    const userId = this._currentUser?.id;
    if (!navigator.onLine && userId) {
      const cached = await offlineStorage.loadMessages(userId, conversationId);
      if (cached) return cached;
      return [];
    }
    try {
      const { data, error } = await this.client
        .from('messages')
        .select('*, reply_to_message:reply_to_id(id, content, sender_id)')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const messages = data.map((json) => {
        const message = Message.fromJson(json);
        if (json.reply_to_message) message.replyToMessage = Message.fromJson(json.reply_to_message);
        return message;
      });
      if (userId) {
        await offlineStorage.saveMessages(
          userId,
          conversationId,
          messages.map((m) => (m.toJson ? m.toJson() : m))
        );
      }
      return messages;
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (userId) {
        const cached = await offlineStorage.loadMessages(userId, conversationId);
        return cached || [];
      }
      return [];
    }
  }

  async sendMessage(conversationId, content, { replyToId } = {}) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: this._currentUser?.id,
          content,
          reply_to_id: replyToId,
        })
        .select()
        .single();
      if (error) throw error;

      const { data: conversationData, error: convError } = await this.client
        .from('conversations')
        .select('client_id, provider_id, clients(full_name), providers(full_name)')
        .eq('id', conversationId)
        .single();
      if (!convError && conversationData) {
        const isCurrentUserClient = conversationData.client_id === this._currentUser?.id;
        const recipientId = isCurrentUserClient
          ? conversationData.provider_id
          : conversationData.client_id;
        const senderName = isCurrentUserClient
          ? conversationData.clients?.full_name || 'عميل'
          : conversationData.providers?.full_name || 'مزود خدمة';
        try {
          const { default: notificationService } = await import('./notificationService');
          await notificationService.notifyNewMessage(recipientId, senderName, content, conversationId);
        } catch (_) {}
      }
      return { data: Message.fromJson(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  async updateMessage(messageId, newContent) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('messages')
        .update({ content: newContent, updated_at: new Date().toISOString() })
        .eq('id', messageId);
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  async deleteMessage(messageId) {
    await this.waitForInit();
    try {
      const msg = await this.client
        .from('messages')
        .select('cloudinary_public_id, media_type')
        .eq('id', messageId)
        .maybeSingle();
      if (msg?.data?.cloudinary_public_id) {
        try {
          await this.client.functions.invoke('delete-cloudinary-media', {
            body: {
              public_id: msg.data.cloudinary_public_id,
              resource_type: msg.data.media_type ?? 'image',
            },
          });
        } catch (_) {}
      }
      const { error } = await this.client.from('messages').delete().eq('id', messageId);
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  async getOrCreateConversation(clientId, providerId) {
    await this.waitForInit();
    try {
      const { data: existing, error: findError } = await this.client
        .from('conversations')
        .select('id')
        .eq('client_id', clientId)
        .eq('provider_id', providerId)
        .maybeSingle();
      if (findError) throw findError;
      if (existing) return existing.id;

      const { data: newConversation, error: createError } = await this.client
        .from('conversations')
        .insert({ client_id: clientId, provider_id: providerId, created_at: new Date().toISOString() })
        .select('id')
        .single();
      if (createError) throw createError;
      return newConversation.id;
    } catch (error) {
      console.error('❌ Error in getOrCreateConversation:', error);
      return null;
    }
  }

  // ✅ مطابقة Dart — deleteConversationWithMedia
  async deleteConversationWithMedia(conversationId) {
    await this.waitForInit();
    try {
      const { data: messages } = await this.client
        .from('messages')
        .select('cloudinary_public_id, media_type')
        .eq('conversation_id', conversationId)
        .not('cloudinary_public_id', 'is', null);
      for (const msg of messages || []) {
        if (msg.cloudinary_public_id) {
          try {
            await this.client.functions.invoke('delete-cloudinary-media', {
              body: { public_id: msg.cloudinary_public_id, resource_type: msg.media_type ?? 'image' },
            });
          } catch (_) {}
        }
      }
      await this.client.from('conversations').delete().eq('id', conversationId);
    } catch (error) {
      throw error;
    }
  }

  // ── Realtime ───────────────────────────────────────────────

  listenToNewMessages(conversationId, onNewMessage) {
    try {
      if (typeof window === 'undefined') return { unsubscribe: () => {} };
      const channel = this.client
        .channel(`messages:${conversationId}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
          (payload) => onNewMessage(Message.fromJson(payload.new))
        )
        .subscribe();
      return channel;
    } catch (error) {
      return { unsubscribe: () => {} };
    }
  }

  listenToNewRequests(providerId, onNewRequest) {
    try {
      if (typeof window === 'undefined') return { unsubscribe: () => {} };
      const channel = this.client
        .channel(`requests:${providerId}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'requests', filter: `provider_id=eq.${providerId}` },
          (payload) => {
            setTimeout(async () => {
              const reqId = payload.new?.id;
              if (reqId) {
                const fullRequest = await this.getRequestById(reqId);
                if (fullRequest) onNewRequest(fullRequest);
              }
            }, 500);
          }
        )
        .subscribe();
      return channel;
    } catch (error) {
      return { unsubscribe: () => {} };
    }
  }

  listenToRequestStatusChanges(userId, onRequestUpdated) {
    try {
      if (typeof window === 'undefined') return { unsubscribe: () => {} };
      const channel = this.client
        .channel(`request_updates:${userId}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'requests', filter: `client_id=eq.${userId}` },
          (payload) => {
            setTimeout(async () => {
              const reqId = payload.new?.id;
              if (reqId) {
                const fullRequest = await this.getRequestById(reqId);
                if (fullRequest) onRequestUpdated(fullRequest);
              }
            }, 500);
          }
        )
        .subscribe();
      return channel;
    } catch (error) {
      return { unsubscribe: () => {} };
    }
  }

  // ✅ مطابقة Dart — listenToProviderRequestUpdates
  listenToProviderRequestUpdates(providerId, onRequestUpdated) {
    try {
      if (typeof window === 'undefined') return { unsubscribe: () => {} };
      const channel = this.client
        .channel(`provider_requests_updates_${providerId}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'requests', filter: `provider_id=eq.${providerId}` },
          (payload) => {
            setTimeout(async () => {
              const reqId = payload.new?.id;
              if (reqId) {
                const fullRequest = await this.getRequestById(reqId);
                if (fullRequest) onRequestUpdated(fullRequest);
              }
            }, 800);
          }
        )
        .subscribe();
      return channel;
    } catch (error) {
      return { unsubscribe: () => {} };
    }
  }

  // ── Notifications ──────────────────────────────────────────

  async createNotification(notification) {
    await this.waitForInit();
    try {
      const rawData = notification.toJson ? notification.toJson() : notification;
      const dbData = {
        user_id: rawData.user_id || rawData.userId,
        title: rawData.title,
        message: rawData.message,
        is_read: rawData.is_read !== undefined ? rawData.is_read : false,
      };
      if (!dbData.user_id || !dbData.title || !dbData.message) return false;
      const { error } = await this.client.from('notifications').insert(dbData);
      if (error) { console.error('❌ خطأ SQL عند حفظ الإشعار:', error); return false; }
      return true;
    } catch (error) {
      console.error('❌ خطأ عام في إنشاء الإشعار:', error);
      return false;
    }
  }

  async getNotificationsForUser(userId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data.map((json) => AppNotification.fromJson(json));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  // ✅ جديد — عدد الإشعارات غير المقروءة
  async getUnreadNotificationsCount(userId) {
    await this.waitForInit();
    try {
      const { count, error } = await this.client
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);
      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error counting unread notifications:', error);
      return 0;
    }
  }

  // ✅ جديد — تعيين جميع الإشعارات كمقروءة
  async markAllNotificationsAsRead(userId) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_read', false);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }

  // ✅ استماع فيديو — realtime للإشعارات الجديدة
  listenToNewNotifications(userId, callback) {
    try {
      if (typeof window === 'undefined') return { unsubscribe: () => {} };
      const channel = this.client
        .channel(`notifications:${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            if (payload.new && callback) {
              callback(AppNotification.fromJson(payload.new));
            }
          }
        )
        .subscribe();
      return channel;
    } catch (error) {
      console.error('listenToNewNotifications error:', error);
      return { unsubscribe: () => {} };
    }
  }

  async markNotificationAsRead(notificationId) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId);
      if (error) throw error;
      return true;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — deleteNotification
  async deleteNotification(notificationId) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('notifications')
        .delete()
        .eq('id', notificationId);
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — deleteAllNotifications
  async deleteAllNotifications(userId) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('notifications')
        .delete()
        .eq('user_id', userId);
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  async saveFCMToken(userId, token) {
    await this.waitForInit();
    try {
      const { error } = await this.client.from('user_tokens').upsert(
        { user_id: userId, fcm_token: token, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error saving FCM token:', error);
      return false;
    }
  }

  async getFCMToken(userId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('user_tokens')
        .select('fcm_token')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data?.fcm_token || null;
    } catch (error) {
      return null;
    }
  }

  // ── Monthly Payments ───────────────────────────────────────

  async getCurrentMonthPayment(providerId) {
    await this.waitForInit();
    try {
      const now = new Date();
      const currentMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      const { data, error } = await this.client
        .from('monthlypayments')
        .select('*')
        .eq('provider_id', providerId)
        .eq('month', currentMonth.toISOString())
        .maybeSingle();
      if (error) throw error;
      return data ? MonthlyPayment.fromJson(data) : null;
    } catch (error) {
      console.error('Error fetching current month payment:', error);
      return null;
    }
  }

  async updateReceiptUrl(providerId, month, receiptUrl) {
    await this.waitForInit();
    try {
      const { data: latestRecord, error: fetchError } = await this.client
        .from('monthlypayments')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;
      if (latestRecord) {
        const { error: updateError } = await this.client
          .from('monthlypayments')
          .update({ receipt_url: receiptUrl, payment_status: 'paid', updated_at: new Date().toISOString() })
          .eq('id', latestRecord.id);
        if (updateError) throw updateError;
        return { success: true, action: 'updated', id: latestRecord.id };
      } else {
        const dateObj = month ? new Date(month) : new Date();
        const targetMonthDate = new Date(Date.UTC(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), 1));
        const { data: newRecord, error: insertError } = await this.client
          .from('monthlypayments')
          .insert({
            provider_id: providerId,
            month: targetMonthDate.toISOString(),
            receipt_url: receiptUrl,
            payment_status: 'paid',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();
        if (insertError) throw insertError;
        return { success: true, action: 'created', id: newRecord.id };
      }
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — getWalletTransactions
  async getWalletTransactions(userId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — recordWalletTransaction
  async recordWalletTransaction({ userId, userType, type, amount, note = null }) {
    await this.waitForInit();
    try {
      const { error } = await this.client.from('wallet_transactions').insert({
        user_id: userId,
        user_type: userType,
        type,
        amount,
        note,
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — updateMonthlyPaymentCommission
  async updateMonthlyPaymentCommission({ providerId, month, paidAmount, isFullyPaid }) {
    await this.waitForInit();
    try {
      const d = new Date(month);
      const monthKey = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
      const { data: current } = await this.client
        .from('monthlypayments')
        .select('commission')
        .eq('provider_id', providerId)
        .eq('month', monthKey)
        .maybeSingle();
      const currentCommission = current?.commission ?? 0;
      const newCommission = Math.max(0, currentCommission - paidAmount);
      const { error } = await this.client
        .from('monthlypayments')
        .update({
          commission: newCommission,
          payment_status: isFullyPaid ? 'paid' : 'pending',
          updated_at: new Date().toISOString(),
        })
        .eq('provider_id', providerId)
        .eq('month', monthKey);
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — getAppConfig
  async getAppConfig() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('app_config')
        .select()
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      return data;
    } catch (error) {
      return null;
    }
  }

  // ── Provider Bank Account ──────────────────────────────────

  // ✅ مطابقة Dart — getProviderBankAccount
  async getProviderBankAccount(providerId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('provider_bank_accounts')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('getProviderBankAccount error:', error);
      return null;
    }
  }

  // ✅ مطابقة Dart — upsertProviderBankAccount
  async upsertProviderBankAccount({ providerId, rib, chequeUrl }) {
    await this.waitForInit();
    try {
      const { data: existing } = await this.client
        .from('provider_bank_accounts')
        .select('id')
        .eq('provider_id', providerId)
        .maybeSingle();
      if (existing) {
        await this.client
          .from('provider_bank_accounts')
          .update({ rib, cheque_url: chequeUrl, is_approved: null, updated_at: new Date().toISOString() })
          .eq('provider_id', providerId);
      } else {
        await this.client
          .from('provider_bank_accounts')
          .insert({ provider_id: providerId, rib, cheque_url: chequeUrl, is_approved: null });
      }
    } catch (error) {
      throw error;
    }
  }

  // ── Wallet Payment ─────────────────────────────────────────

  async freezeWalletPayment({ clientId, amount, requestId }) {
    await this.waitForInit();
    try {
      const { error } = await this.client.rpc('freeze_wallet_payment', {
        p_client_id: clientId,
        p_amount: amount,
        p_request_id: requestId,
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — unfreezeWalletPayment
  async unfreezeWalletPayment({ clientId, amount, requestId }) {
    await this.waitForInit();
    try {
      const { error } = await this.client.rpc('unfreeze_wallet_payment', {
        p_client_id: clientId,
        p_amount: amount,
        p_request_id: requestId,
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — processWalletPayment
  async processWalletPayment({ clientId, providerId, amount, requestId }) {
    await this.waitForInit();
    try {
      const { error } = await this.client.rpc('process_wallet_payment', {
        p_client_id: clientId,
        p_provider_id: providerId,
        p_amount: amount,
        p_request_id: requestId,
      });
      if (error) {
        if (error.message?.includes('already_paid')) return;
        throw error;
      }
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — getProfessionCommissionRate
  async getProfessionCommissionRate(professionId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('professions')
        .select('commission_rate')
        .eq('id', professionId)
        .maybeSingle();
      if (error) throw error;
      return data?.commission_rate ?? 10.0;
    } catch (error) {
      return 10.0;
    }
  }

  // ✅ إنشاء طلب شحن عبر بريدي موب (يحتاج مراجعة إدارية)
  async createWalletRecharge({ userId, amount, receiptUrl }) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('wallet_recharges')
        .insert({
          user_id: userId,
          amount,
          receipt_url: receiptUrl,
          status: 'pending',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  // ✅ جلب كل طلبات الشحن عبر بريدي موب للمستخدم (سجل كامل + لمعرفة إن كان هناك طلب pending)
  async getWalletRecharges(userId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('wallet_recharges')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('getWalletRecharges error:', error);
      return [];
    }
  }

  // ✅ جديد — Debt Recovery System

  // حساب إجمالي الديون المعلقة (pending) لمزود أو زبون
  async getPendingCommissionsTotal(userId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('monthlypayments')
        .select('commission')
        .eq('provider_id', userId)
        .eq('payment_status', 'pending');
      if (error) throw error;
      const total = (data || []).reduce(
        (sum, row) => sum + (parseFloat(row.commission) || 0),
        0
      );
      return total;
    } catch (error) {
      console.error('getPendingCommissionsTotal error:', error);
      return 0;
    }
  }

  // الحصول على قائمة الفواتير المعلقة لعرضها وربط الوصل
  async getPendingPayments(userId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('monthlypayments')
        .select('id, provider_id, month, commission, net_earnings, total_earnings, payment_status, receipt_url, created_at, updated_at')
        .eq('provider_id', userId)
        .eq('payment_status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('getPendingPayments error:', error);
      return [];
    }
  }

  // رفع وصل الدفع (صورة) إلى التخزين
  async uploadReceipt(file) {
    await this.waitForInit();
    try {
      const uploadedUrl = await cloudinaryService.uploadImage(file, 'receipts');
      return uploadedUrl;
    } catch (error) {
      console.error('uploadReceipt error:', error);
      throw error;
    }
  }

  // ربط وصل الدفع بربط الفاتورة وتحديث حالتها إلى "submitted"
  async submitPaymentReceipt(paymentId, receiptUrl) {
    await this.waitForInit();
    try {
      const { error } = await this.client
        .from('monthlypayments')
        .update({
          receipt_url: receiptUrl,
          payment_status: 'submitted',
          updated_at: new Date().toISOString(),
        })
        .eq('id', paymentId);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('submitPaymentReceipt error:', error);
      throw error;
    }
  }

  // ✅ مطابقة Dart — createComplaint
  async createComplaint(complaint) {

    await this.waitForInit();
    try {
      const data = complaint.toJson ? complaint.toJson() : complaint;
      const { error } = await this.client.from('complaints').insert(data);
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  // ── Digital Products ───────────────────────────────────────

  // ✅ مطابقة Dart — purchaseDigitalProduct
  async purchaseDigitalProduct({ clientId, serviceId, providerId, amount }) {
    await this.waitForInit();
    try {
      const { error } = await this.client.rpc('purchase_digital_product', {
        p_client_id: clientId,
        p_service_id: serviceId,
        p_provider_id: providerId,
        p_amount: amount,
      });
      if (error) {
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('insufficient_balance')) throw new Error('رصيدك غير كافٍ لإتمام الشراء');
        if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('conflict') || msg.includes('already'))
          throw new Error('اشتريت هذا المنتج مسبقاً');
        throw new Error(error.message);
      }
    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('insufficient')) throw new Error('رصيدك غير كافٍ');
      if (msg.includes('already') || msg.includes('duplicate')) throw new Error('اشتريت هذا المنتج مسبقاً');
      throw error;
    }
  }

  // ✅ مطابقة Dart — hasClientPurchased
  async hasClientPurchased({ clientId, serviceId }) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('digital_purchases')
        .select('id')
        .eq('client_id', clientId)
        .eq('service_id', serviceId)
        .maybeSingle();
      if (error) throw error;
      return data !== null;
    } catch (error) {
      console.error('hasClientPurchased error:', error);
      return false;
    }
  }

  // ✅ مطابقة Dart — getClientLibrary
  async getClientLibrary(clientId) {
    await this.waitForInit();
    try {
      const { data: purchases, error } = await this.client
        .from('digital_purchases')
        .select('id, client_id, service_id, provider_id, amount, purchased_at, is_active')
        .eq('client_id', clientId)
        .eq('is_active', true)
        .order('purchased_at', { ascending: false });
      if (error) throw error;
      if (!purchases.length) return [];

      const serviceIds = purchases.map((p) => p.service_id);
      const providerIds = purchases.map((p) => p.provider_id);

      const { data: servicesResp } = await this.client
        .from('services')
        .select('id, name, description, price, image_url, file_url, preview_url, average_rating, product_type, preview_images, media_urls, is_active, allow_download')
        .in('id', serviceIds);
      const { data: providersResp } = await this.client
        .from('providers')
        .select('id, full_name, avatar_url')
        .in('id', providerIds);

      const servicesMap = Object.fromEntries((servicesResp || []).map((s) => [s.id, s]));
      const providersMap = Object.fromEntries((providersResp || []).map((p) => [p.id, p]));

      return purchases.map((p) => ({
        ...p,
        services: servicesMap[p.service_id] || null,
        providers: providersMap[p.provider_id] || null,
      }));
    } catch (error) {
      console.error('❌ Error getClientLibrary:', error);
      return [];
    }
  }

  // ✅ مطابقة Dart — getClientLibraryByType
  async getClientLibraryByType(clientId, productType) {
    const all = await this.getClientLibrary(clientId);
    return all.filter((p) => p.services?.product_type === productType);
  }

  // ✅ مطابقة Dart — rateDigitalProduct
  async rateDigitalProduct({ serviceId, clientId, rating }) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('digital_purchases')
        .update({ rating })
        .eq('service_id', serviceId)
        .eq('client_id', clientId)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('لم يتم العثور على الشراء');
      return data;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — getDigitalProductRating
  async getDigitalProductRating({ serviceId, clientId }) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('digital_purchases')
        .select('id, rating')
        .eq('service_id', serviceId)
        .eq('client_id', clientId)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data?.rating ?? null;
    } catch (error) {
      console.error('getDigitalProductRating error:', error);
      return null;
    }
  }

  // ✅ مطابقة Dart — createDigitalComplaint
  async createDigitalComplaint({ clientId, serviceId, providerId, purchaseId, content }) {
    await this.waitForInit();
    try {
      const { error } = await this.client.from('digital_product_complaints').insert({
        client_id: clientId,
        service_id: serviceId,
        provider_id: providerId,
        purchase_id: purchaseId,
        content,
        status: 'pending',
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }

  // ✅ مطابقة Dart — getDigitalProductCategories
  async getDigitalProductCategories() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('digital_product_categories')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('getDigitalProductCategories error:', error);
      return [];
    }
  }

  // ✅ مطابقة Dart — getStoreProviders
  async getStoreProviders() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('providers')
        .select('*, states(name), professions(name, is_store, is_digital_products)')
        .eq('is_verified', true)
        .eq('available', true);
      if (error) throw error;
      return data
        .map((json) => Provider.fromJson(json))
        .filter((p) => p.isStore === true || p.is_store === true);
    } catch (error) {
      console.error('getStoreProviders error:', error);
      return [];
    }
  }

  // ✅ جديد — getFoodProviders
  async getFoodProviders() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('providers')
        .select('*, states(name), professions(name, is_store, is_digital_products, is_food)')
        .eq('is_verified', true)
        .eq('available', true);
      if (error) throw error;
      return data
        .map((json) => Provider.fromJson(json))
        .filter((p) => p.isFood === true || p.is_food === true || p.professionId === 44);
    } catch (error) {
      console.error('getFoodProviders error:', error);
      return [];
    }
  }

  // ✅ مطابقة Dart — getAdIds
  async getAdIds() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('app_config_ads')
        .select('ad_ids')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      return data?.ad_ids || [];
    } catch (error) {
      console.error('getAdIds error:', error);
      return [];
    }
  }

  // ✅ مطابقة Dart — getCategories
  async getCategories() {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('categories')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    } catch (error) {
      return [];
    }
  }

  async getOrCreateCategoryByName(name) {
    if (!name) return null;
    await this.waitForInit();
    try {
      const cleanName = name.trim();
      if (!cleanName) return null;

      // 1. Find if it already exists
      const { data: existing, error: findError } = await this.client
        .from('categories')
        .select('id')
        .eq('name', cleanName)
        .maybeSingle();

      if (findError) throw findError;
      if (existing) return existing.id;

      // 2. If not, insert it
      const { data: inserted, error: insertError } = await this.client
        .from('categories')
        .insert({ name: cleanName })
        .select('id')
        .single();

      if (insertError) throw insertError;
      return inserted.id;
    } catch (error) {
      console.error('getOrCreateCategoryByName error:', error);
      return null;
    }
  }

  // ✅ مطابقة Dart — generateQrToken
  async generateQrToken(serviceId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client.rpc('generate_qr_token', {
        p_service_id: serviceId,
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  async transferFunds(senderId, receiverCode, amount) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client.rpc('transfer_funds', {
        p_sender_id: senderId,
        p_receiver_code: receiverCode,
        p_amount: parseFloat(amount)
      });
      if (error) throw error;

      // Send notification if receiver_id is returned
      if (data && data.length > 0 && data[0].receiver_id) {
        const receiverId = data[0].receiver_id;
        await this.createNotification({
          user_id: receiverId,
          title: 'استلام رصيد',
          message: `تم تحويل مبلغ ${amount} دج إلى محفظتك.`,
          is_read: false,
        }).catch(err => console.error("Error creating notification for transfer:", err));
      }

      return true;
    } catch (error) {
      console.error('transferFunds error:', error);
      throw error;
    }
  }

  async createComplaint({ clientId, providerId, requestId, reason }) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('complaints')
        .insert({
          client_id: clientId,
          provider_id: providerId,
          request_id: requestId,
          reason,
          status: 'pending',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('createComplaint error:', error);
      throw error;
    }
  }

  // ✅ جلب تفاصيل الخدمة مع معلومات المزود للشراء بـ QR
  async getServiceDetails(serviceId) {
    await this.waitForInit();
    try {
      const { data: serviceJson, error: serviceError } = await this.client
        .from('services')
        .select('*, categories(name), digital_category_id, product_type, file_url, preview_url, preview_images, media_urls')
        .eq('id', serviceId)
        .eq('is_active', true)
        .single();
      if (serviceError) throw serviceError;
      if (!serviceJson) return null;

      const service = Service.fromJson(serviceJson);
      if (!service.imageUrl && serviceJson.image_url) service.imageUrl = serviceJson.image_url;

      const { data: providerJson, error: providerError } = await this.client
        .from('providers')
        .select('id, full_name, avatar_url, wallet')
        .eq('id', service.providerId)
        .single();

      if (providerError) {
        console.warn('Error fetching provider details:', providerError);
      }

      return {
        service,
        provider: providerJson ? {
          id: providerJson.id,
          fullName: providerJson.full_name,
          avatarUrl: providerJson.avatar_url,
          wallet: providerJson.wallet
        } : null
      };
    } catch (error) {
      console.error('Error in getServiceDetails:', error);
      throw error;
    }
  }

  // ✅ تنفيذ الشراء عبر QR
  async purchaseViaQr(clientId, serviceId) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client.rpc('purchase_via_qr', {
        p_client_id: clientId,
        p_service_id: serviceId,
      });
      if (error) {
        throw error;
      }
      return data; // { success: boolean, message: string }
    } catch (error) {
      console.error('Error in purchaseViaQr:', error);
      throw error;
    }
  }

  // ── Personal Wallet QR Transfer ──────────────────────────

  // ✅ الحصول على مزود عن طريق wallet_code
  async getProviderByWalletCode(walletCode) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('providers')
        .select('*')
        .eq('wallet_code', walletCode)
        .maybeSingle();
      if (error) throw error;
      return data ? Provider.fromJson(data) : null;
    } catch (error) {
      console.error('getProviderByWalletCode error:', error);
      return null;
    }
  }

  // ✅ الحصول على زبون عن طريق wallet_code
  async getClientByWalletCode(walletCode) {
    await this.waitForInit();
    try {
      const { data, error } = await this.client
        .from('clients')
        .select('*')
        .eq('wallet_code', walletCode)
        .maybeSingle();
      if (error) throw error;
      return data ? Client.fromJson(data) : null;
    } catch (error) {
      console.error('getClientByWalletCode error:', error);
      return null;
    }
  }

  // ✅ البحث عن أي مستخدم (مزود أو زبون) عبر wallet_code
  // يعيد { userType: 'provider'|'client', user: Provider|Client } أو null
  async getUserByWalletCode(walletCode) {
    await this.waitForInit();
    // جرب المزود أولاً
    const provider = await this.getProviderByWalletCode(walletCode);
    if (provider) return { userType: 'provider', user: provider };
    // جرب الزبون
    const client = await this.getClientByWalletCode(walletCode);
    if (client) return { userType: 'client', user: client };
    return null;
  }

  // ✅ استماع实时 لتغييرات المحفظة (الرصيد)
  listenToWalletChanges(userId, onTransaction) {
    try {
      if (typeof window === 'undefined') return { unsubscribe: () => {} };
      const channel = this.client
        .channel(`wallet:${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'wallet_transactions',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            if (payload.new && onTransaction) {
              onTransaction(payload.new);
            }
          }
        )
        .subscribe();
      return channel;
    } catch (error) {
      console.error('listenToWalletChanges error:', error);
      return { unsubscribe: () => {} };
    }
  }

  cleanup() {
    if (this._authListener) {
      this._authListener.subscription.unsubscribe();
    }
  }
}

const supabaseServiceInstance = new SupabaseService();
export default supabaseServiceInstance;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    supabaseServiceInstance.cleanup();
  });
}